<template>
  <div>
    <div class="mb-10 flex flex-col md:flex-row lg:mb-15">
      <div>
        <div class="text-2xl font-normal md:text-5xl">
          <h1 v-if="!editMode">
            {{ myAlbumName }}
          </h1>
          <input
            type="text"
            v-else
            v-model.trim.lazy="myAlbumName"
            @keyup.enter="editMode = false"
            @blur="editMode = false"
            ref="albumNameInput"
          />
        </div>

        <my-album-action-component
          :my-album-name="myAlbumName"
          :shared-album="!!albumId"
          @editMyAlbumName="editMyAlbumName"
        ></my-album-action-component>
      </div>
      <div class="order-first md:order-last md:ml-auto">
        <slot></slot>
      </div>
    </div>

    <ul v-if="objects" class="row layout-normal">
      <li
        v-for="object in objects"
        :key="object.id"
        class="object-teaser-wrapper"
      >
        <object-teaser-component
          :object="object"
          :sibling-ids="objects.map((o) => o.id)"
        ></object-teaser-component>
      </li>
    </ul>
  </div>
</template>

<script lang="ts">
import {
  defineComponent,
  getCurrentInstance,
  onMounted,
  ref,
  Ref,
  watch,
} from 'vue';
import { IdFilter } from '../@types/filter';
import myAlbumQuery from '../gql/myAlbumQuery.gql';
import objectsTeaserQuery from '../gql/objectsTeaserQuery.gql';
import { createApi, executeGqlQuery } from '../utils/gql';
import { getItem, setItem } from '../utils/localStorage';
import { dispatchWindowEvent, getGetParam, lang } from '../utils/utils';
import MyAlbumActionComponent from './MyAlbumActionComponent.vue';
import ObjectTeaserComponent from './ObjectTeaserComponent.vue';

export default defineComponent({
  components: {
    MyAlbumActionComponent,
    ObjectTeaserComponent,
  },

  setup() {
    const instance = getCurrentInstance();
    const defaultMyAlbumName = lang === 'de' ? 'Mein Album' : 'My Album';
    const albumId = getGetParam('id');
    const myAlbumName: Ref<any> = ref(!albumId ? defaultMyAlbumName : '');
    const api = createApi();
    const editMode = ref(false);
    const albumNameInput: Ref<HTMLInputElement | null> = ref(null);

    onMounted(() => {
      if (!albumId) {
        myAlbumName.value = getItem('myAlbumName') || myAlbumName.value;
      }
    });

    const editMyAlbumName = () => {
      editMode.value = true;

      instance?.proxy?.$nextTick(() => {
        if (albumNameInput.value) {
          albumNameInput.value.focus();
        }
      });
    };

    watch(
      myAlbumName,
      (value) => {
        if (value !== '') {
          setItem('myAlbumName', value);
        } else {
          myAlbumName.value = 'My Album';
        }
      },
      { immediate: false }
    );

    const objects = ref([]);

    if (albumId) {
      executeGqlQuery(
        api,
        myAlbumQuery,
        {
          id: albumId,
        },
        (data: any) => {
          myAlbumName.value = data.myAlbum.name;
          const { objectIds } = data.myAlbum;

          const filter: IdFilter = { ids: objectIds as string[] };

          executeGqlQuery(
            api,
            objectsTeaserQuery,
            {
              filter,
              lang: lang.toUpperCase(),
              caption: true,
            },
            ({ objects: result }: { objects: any }) => {
              objects.value = result.objects;

              instance?.proxy.$nextTick(() => {
                dispatchWindowEvent('matchheights');
              });
            }
          );
        }
      );
    } else {
      const starredObjectsString = getItem('starredObjects');
      let starredObjects = [];

      if (starredObjectsString && JSON.parse(starredObjectsString).length) {
        starredObjects = JSON.parse(starredObjectsString) as number[];
        const objectIds = starredObjects.map((id: number) => `${id}`);
        const filter: IdFilter = { ids: objectIds };

        executeGqlQuery(
          api,
          objectsTeaserQuery,
          {
            filter,
            lang: lang.toUpperCase(),
            caption: true,
          },
          ({ objects: result }: { objects: any }) => {
            objects.value = result.objects;

            instance?.proxy.$nextTick(() => {
              dispatchWindowEvent('matchheights');
            });
          }
        );
      }
    }

    return {
      objects,
      myAlbumName,
      editMyAlbumName,
      albumId,
      editMode,
      albumNameInput,
    };
  },
});
</script>
